"use client";
import Link from "next/link";
import React, { useState } from "react";
import { StaticImageData } from "next/image";
import { ChevronDown } from "lucide-react";
import workManagement from "@/assets/images/icons/navbar/AllProducts/work-management.svg";
import goalSolution from "@/assets/images/icons/navbar/AllProducts/goal-solution.svg";
import projectSolution from "@/assets/images/icons/navbar/AllProducts/project-solution.svg";
import taskSolution from "@/assets/images/icons/navbar/AllProducts/task-solution.svg";
import chatSolution from "@/assets/images/icons/navbar/AllProducts/chat-solution.svg";
import docSolution from "@/assets/images/icons/navbar/AllProducts/doc-solution.svg";
import formsSolution from "@/assets/images/icons/navbar/AllProducts/forms-solution.svg";
import trackTimeSolution from "@/assets/images/icons/navbar/AllProducts/track-time-solution.svg";
import portfolioSolution from "@/assets/images/icons/navbar/AllProducts/portfolio-solution.svg";
import automationsSolution from "@/assets/images/icons/navbar/AllProducts/automations-solution.svg";
import summarizer from "@/assets/images/icons/navbar/AiTools/summarizer.svg";
import translator from "@/assets/images/icons/navbar/AiTools/translator.svg";
import paraphraser from "@/assets/images/icons/navbar/AiTools/paraphraser.svg";
import grammarChecker from "@/assets/images/icons/navbar/AiTools/grammar-checker.svg";
import chatWithPdf from "@/assets/images/icons/navbar/AiTools/chat-with-pdf.svg";
import projectManagement from "@/assets/images/icons/navbar/solutions/project-management.svg";
import programManagement from "@/assets/images/icons/navbar/solutions/program-management.svg";
import hrRecruitment from "@/assets/images/icons/navbar/solutions/hr-recruitment.svg";
import businessOperations from "@/assets/images/icons/navbar/solutions/business-operations.svg";
import education from "@/assets/images/icons/navbar/solutions/education.svg";
import agencies from "@/assets/images/icons/navbar/solutions/agency.svg";
import manufacturing from "@/assets/images/icons/navbar/solutions/manufacturing.svg";
import realEstate from "@/assets/images/icons/navbar/solutions/realestate.svg";
import healthCare from "@/assets/images/icons/navbar/solutions/healthcare.svg";
import itConsulting from "@/assets/images/icons/navbar/solutions/it-consulting.svg";
import mediaIndustries from "@/assets/images/icons/navbar/solutions/media.svg";
import travelAndHospitality from "@/assets/images/icons/navbar/solutions/travel.svg";
import finance from "@/assets/images/icons/navbar/solutions/finance.svg";
import retail from "@/assets/images/icons/navbar/solutions/retail.svg";
import marketing from "@/assets/images/icons/navbar/solutions/marketing.svg";
import productManagement from "@/assets/images/icons/navbar/solutions/product-management.svg";
import engineering from "@/assets/images/icons/navbar/solutions/engineering.svg";
import helpCenter from "@/assets/images/icons/navbar/resources/help-center.svg";
import blog from "@/assets/images/icons/navbar/resources/blog.svg";
import video from "@/assets/images/icons/navbar/resources/videos.svg";
import asanaLogo from "@/assets/images/comparison/brands/asana.svg";
import jiraLogo from "@/assets/images/comparison/brands/jira.svg";
import mondayLogo from "@/assets/images/comparison/brands/monday.svg";
import msPlannerLogo from "@/assets/images/comparison/brands/ms-planner.svg";
import smartsheetLogo from "@/assets/images/comparison/brands/smartsheet.svg";
import clickupLogo from "@/assets/images/comparison/brands/clickup.svg";
import azureDevopsLogo from "@/assets/images/comparison/brands/azure.svg";
import gleanLogo from "@/assets/images/comparison/brands/glean.svg";
import microsoftCopilotLogo from "@/assets/images/comparison/brands/microsoft-copilot.svg";

// Kroolo Ai Links
import aiAgentImage from "@/assets/images/icons/navbar/ai-agents.svg";
import enterpriseSearchImage from "@/assets/images/icons/navbar/enterprise-search.svg";
import krooloAiImage from "@/assets/images/icons/navbar/kroolo-ai.svg";
import workManagementImage from "@/assets/images/icons/navbar/work-management.svg";
import aiAgentIcon from "@/assets/images/icons/navbar/ai-tab-icon.svg";
import enterpriseSearchIcon from "@/assets/images/icons/navbar/AllProducts/doc-solution.svg";
import krooloAiIcon from "@/assets/images/icons/navbar/AllProducts/ai-solution.svg";

export type LinkObjectType = {
  title: string;
  targetUrl: string;
  hasContent?: boolean;
  openSeperate?: boolean;
  innerLinks?: Array<{
    label?: string;
    url: string;
    subtext?: string;
    icon?: string;
  }>;
  krooloLinks?: Array<{
    label: string;
    url: string;
    icon: StaticImageData;
    image?: StaticImageData;
    subtext?: string;
  }>;
  allProductLinks?: Array<{
    label: string;
    url: string;
    subtext: string;
    icon: StaticImageData;
  }>;
  aiToolsLinks?: Array<{
    label: string;
    url: string;
    subtext: string;
    subtext2: string;
    icon: StaticImageData;
  }>;
  integrationLinks?: Array<{
    label: string;
    url: string;
    icon: StaticImageData;
  }>;
  solutionLinks?: Array<{
    label: string;
    url: string;
    icon: StaticImageData;
    subtext?: string;
  }>;
  industriesLinks?: Array<{
    label: string;
    url: string;
    icon: StaticImageData;
    subtext?: string;
  }>;
  compareLinks?: Array<{
    label: string;
    url: string;
    icon: StaticImageData;
  }>;
  krooloAiLinks?: Array<{
    label: string;
    url: string;
    icon: StaticImageData;
    image?: StaticImageData;
    subtext?: string;
  }>;
};

export const navLinksList: LinkObjectType[] = [
  // 1. PRODUCTS (structure aligned to sitemap)
  {
    title: "Products",
    targetUrl: "",
    hasContent: true,
    krooloLinks: [
      {
        label: "Kroolo Work",
        url: "/work-management",
        icon: workManagement,
        image: workManagementImage,
        subtext:
          "Plan, execute, and track all your work in one workspace.",
      },
      {
        label: "Kroolo Search",
        url: "/ai/enterprise-search",
        icon: enterpriseSearchIcon,
        image: enterpriseSearchImage,
        subtext:
          "Search across projects, docs, and conversations in seconds.",
      },
    ],
    allProductLinks: [
      {
        label: "Project & Sprints",
        url: "/features/projects",
        icon: projectSolution,
        subtext: "Intelligent AI-based project management software for teams",
      },
      {
        label: "Tasks",
        url: "/features/tasks",
        icon: taskSolution,
        subtext: "Intuitive AI-led task management software for everyone",
      },
      {
        label: "Goal & OKRs",
        url: "/features/goals",
        icon: goalSolution,
        subtext: "AI-based Goal & OKR performance management tool for teams",
      },
      {
        label: "Chats",
        url: "/features/chats",
        icon: chatSolution,
        subtext: "Slack-like team collaboration software for teams",
      },
      {
        label: "Track Time",
        url: "/features/track-time",
        icon: trackTimeSolution,
        subtext:
          "Seamlessly track time, manage client expenses, and handle invoicing in one place",
      },
      {
        label: "Portfolio",
        url: "/features/portfolio",
        icon: portfolioSolution,
        subtext:
          "Manage, collaborate, and track all your projects in one centralized portfolio",
      },
      {
        label: "Automations",
        url: "/features/automations",
        icon: automationsSolution,
        subtext:
          "Use over 100 prebuilt templates to streamline and automate your tasks.",
      },
      {
        label: "Docs",
        url: "/features/docs",
        icon: docSolution,
        subtext: "Advanced AI-based Document management software for you",
      },
      {
        label: "Forms",
        url: "/features/forms",
        icon: formsSolution,
        subtext: "Create your own form for your project",
      },
    ],
    aiToolsLinks: [
      {
        label: "Grammar Checker",
        url: "/ai-tools/grammar-checker",
        icon: grammarChecker,
        subtext:
          "Check your English grammar and eliminate mistakes in your writing.",
        subtext2: "Easy to use Grammar checks",
      },
      {
        label: "Paraphraser",
        url: "/ai-tools/paraphraser",
        icon: paraphraser,
        subtext:
          "Quickly rephrase and reword any text for essays, articles, emails, and more.",
        subtext2: "Paraphrase anything and improve quality of content",
      },
      {
        label: "Summarizer",
        url: "/ai-tools/summarizer",
        icon: summarizer,
        subtext:
          "Condense long articles, research papers, or documents into concise paragraphs.",
        subtext2: "Summarize any type of content",
      },
      {
        label: "Translator",
        url: "/ai-tools/translator",
        icon: translator,
        subtext:
          "Instantly translate words and phrases into multiple languages within seconds.",
        subtext2: "Translate any content in 40+ languages",
      },
      {
        label: "Chat With Pdf",
        url: "/ai-tools/chat-with-pdf",
        icon: chatWithPdf,
        subtext:
          "Upload any PDF and ask questions about it to get instant answers.",
        subtext2: "Chat with your PDF in real-time",
      },
    ],
    integrationLinks: [],
    compareLinks: [
      {
        label: "Asana",
        url: "/compare/kroolo-vs-asana",
        icon: asanaLogo,
      },
      {
        label: "Azure DevOps",
        url: "/compare/kroolo-vs-azure-devops",
        icon: azureDevopsLogo,
      },
      {
        label: "ClickUp",
        url: "/compare/kroolo-vs-clickup",
        icon: clickupLogo,
      },
      {
        label: "Jira Cloud",
        url: "/compare/kroolo-vs-jira",
        icon: jiraLogo,
      },
      {
        label: "Monday",
        url: "/compare/kroolo-vs-monday",
        icon: mondayLogo,
      },
      {
        label: "MS Planner",
        url: "/compare/kroolo-vs-microsoft-planner",
        icon: msPlannerLogo,
      },
      {
        label: "Smartsheet",
        url: "/compare/kroolo-vs-smartsheet",
        icon: smartsheetLogo,
      },
      {
        label: "Glean",
        url: "/compare/kroolo-vs-glean",
        icon: gleanLogo,
      },
      {
        label: "Microsoft Copilot",
        url: "/compare/kroolo-vs-microsoft-copilot",
        icon: microsoftCopilotLogo,
      },
    ],
  },
  {
    title: "Solutions",
    targetUrl: "",
    hasContent: true,
    solutionLinks: [
      {
        label: "Project Management",
        icon: projectManagement,
        url: "/solutions/project-management",
      },
      {
        label: "Product Management",
        icon: productManagement,
        url: "/solutions/product-management",
      },
      {
        label: "Marketing",
        icon: marketing,
        url: "/solutions/marketing",
      },
      {
        label: "Engineering",
        icon: engineering,
        url: "/solutions/engineering",
      },
      {
        label: "Support & Business Operations",
        icon: businessOperations,
        url: "/solutions/business-operations",
      },
      {
        label: "HR & Recruitment",
        icon: hrRecruitment,
        url: "/solutions/hr-recruitment",
      },
      {
        label: "Education",
        icon: education,
        url: "/solutions/k12-education",
      },
      {
        label: "Program Management",
        icon: programManagement,
        url: "/solutions/program-management",
      },
    ],
    industriesLinks: [
      {
        label: "Agencies",
        url: "/industries/agencies",
        icon: agencies,
      },
      {
        label: "Financial Services",
        url: "/industries/financial-services",
        icon: finance,
      },
      {
        label: "Retail",
        url: "/industries/retail",
        icon: retail,
      },
      {
        label: "Manufacturing",
        url: "/industries/manufacturing",
        icon: manufacturing,
      },
      {
        label: "Education",
        url: "/industries/education",
        icon: education,
      },
      {
        label: "Real Estate",
        url: "/industries/real-estate",
        icon: realEstate,
      },
      {
        label: "IT Consulting",
        url: "/industries/it-consulting",
        icon: itConsulting,
      },
      {
        label: "Healthcare",
        url: "/industries/healthcare",
        icon: healthCare,
      },
      {
        label: "Travel and Hospitality",
        url: "/industries/travel-and-hospitality",
        icon: travelAndHospitality,
      },
      {
        label: "Media",
        url: "/industries/media",
        icon: mediaIndustries,
      },
    ],
  },
  {
    title: "Pricing",
    targetUrl: "/pricing",
  },
  {
    title: "Resources",
    targetUrl: "/resources",
    hasContent: true,
    openSeperate: true,
    innerLinks: [
      {
        label: "Help center",
        url: "https://help.kroolo.com/en/",
        icon: helpCenter,
        subtext: "Visit Help Center for answers, guides, and support.",
      },

      {
        label: "Blog",
        url: "/blog",
        icon: blog,
        subtext: "Explore blog for tips, guides, insights, and service help.",
      },
      {
        label: "Videos",
        url: "/videos",
        icon: video,
        subtext: "Watch videos to learn Kroolo features.",
      },
    ],
  },
  {
    title: "Kroolo AI",
    targetUrl: "/features/ai",
    hasContent: true,
    krooloAiLinks: [
      {
        label: "AI Agents",
        url: "/ai/ai-agent",
        icon: aiAgentIcon,
        image: aiAgentImage,
        subtext:
          "AI agents to plan, execute, and automate work across your org.",
      },
      {
        label: "Kroolo Ai",
        url: "/features/ai",
        icon: krooloAiIcon,
        image: krooloAiImage,
        subtext: "AI-powered tools for your business.",
      },
    ],
    aiToolsLinks: [
      {
        label: "Grammar Checker",
        url: "/ai-tools/grammar-checker",
        icon: grammarChecker,
        subtext:
          "Check your English grammar and eliminate mistakes in your writing.",
        subtext2: "Easy to use Grammar checks",
      },
      {
        label: "Paraphraser",
        url: "/ai-tools/paraphraser",
        icon: paraphraser,
        subtext:
          "Quickly rephrase and reword any text for essays, articles, emails, and more.",
        subtext2: "Paraphrase anything and improve quality of content",
      },
      {
        label: "Summarizer",
        url: "/ai-tools/summarizer",
        icon: summarizer,
        subtext:
          "Condense long articles, research papers, or documents into concise paragraphs.",
        subtext2: "Summarize any type of content",
      },
      {
        label: "Translator",
        url: "/ai-tools/translator",
        icon: translator,
        subtext:
          "Instantly translate words and phrases into multiple languages within seconds.",
        subtext2: "Translate any content in 40+ languages",
      },
      {
        label: "Chat With Pdf",
        url: "/ai-tools/chat-with-pdf",
        icon: chatWithPdf,
        subtext:
          "Upload any PDF and ask questions about it to get instant answers.",
        subtext2: "Chat with your PDF in real-time",
      },
    ],
  },
];

const NavLinks = ({
  setHoveredArea,
  setExpandableArea,
  hoveredArea,
  setForceRenderValue,
}: {
  setHoveredArea: React.Dispatch<React.SetStateAction<string>>;
  setExpandableArea: React.Dispatch<React.SetStateAction<number | undefined>>;
  setForceRenderValue: React.Dispatch<React.SetStateAction<number>>;
  hoveredArea: string;
}) => {
  const [localHover, setLocalHover] = useState<string | null>(null);
  const titleStyle = ` text-[14px] font-medium hover:opacity-100`;

  const mouseLeaveFunc = () => {
    setExpandableArea(undefined);
    setLocalHover(null);
    setTimeout(() => {
      if (!hoveredArea) setHoveredArea("");
    }, 400);
  };

  const handleMouseOver = (title: string) => {
    setForceRenderValue((prev) => prev + 1);
    setHoveredArea(title.toLowerCase());
    setLocalHover(title.toLowerCase());
  };
  const orderedNavLinks = React.useMemo(() => {
    const order: Record<string, number> = {
      products: 1,
      solutions: 2,
      "kroolo ai": 3,
      pricing: 4,
      resources: 5,
    };
    return [...navLinksList].sort((a, b) => {
      const aKey = a.title.toLowerCase();
      const bKey = b.title.toLowerCase();
      return (order[aKey] || 99) - (order[bKey] || 99);
    });
  }, []);

  return (
    <div className="ml-auto mr-auto flex items-center min-[1200px]:gap-8 min-[1300px]:gap-14">
      {orderedNavLinks.map((item, index) => (
        <div key={index} className="py-5">
          {item.hasContent ? (
            <div
              className="flex items-center text-[#343434] duration-300 hover:text-[#6049E7]"
              onMouseOver={() => handleMouseOver(item.title)}
              onMouseLeave={mouseLeaveFunc}
            >
              <span className={titleStyle}>{item.title}</span>
              <ChevronDown
                className={`ml-1 h-4 w-4 transform transition-transform duration-300 ${localHover === item.title.toLowerCase() ? "-scale-y-100" : ""
                  }`}
              />
            </div>
          ) : (
            <Link
              className={`${titleStyle} text-[#343434] duration-300 hover:text-[#6049E7]`}
              href={item.targetUrl}
            >
              {item.title}
            </Link>
          )}
        </div>
      ))}
    </div>
  );
};

export default NavLinks;
